import type { RowData } from '@tanstack/react-table';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';

import { useTranslation } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';

import type { DataTableInstance } from './use-data-table';

export interface DataTablePaginationProps<TData extends RowData> {
  table: DataTableInstance<TData>;
  pageSizeOptions?: readonly number[];
}

function DataTablePagination<TData extends RowData>({
  table,
  pageSizeOptions = [10, 20, 50, 100],
}: DataTablePaginationProps<TData>) {
  const { t } = useTranslation('common');
  const { pageIndex, pageSize } = table.pagination;
  const rowCount = table.rowCount ?? table.rows.length;
  const pageCount = Math.max(Math.ceil(rowCount / pageSize), 1);
  const from = rowCount === 0 ? 0 : pageIndex * pageSize + 1;
  const to = Math.min((pageIndex + 1) * pageSize, rowCount);
  const go = (index: number) => table.setPagination({ pageIndex: index, pageSize });

  return (
    <nav
      aria-label={t('indexTable.pagination.label')}
      data-slot="data-table-pagination"
      className="text-muted-foreground flex flex-wrap items-center justify-between gap-3 border-t px-3 py-2 text-sm"
    >
      <div className="flex items-center gap-2">
        <span aria-hidden>{t('table.rowsPerPage')}</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              aria-label={t('indexTable.pagination.pageSize', { count: pageSize })}
            >
              {pageSize}
              <ChevronDownIcon aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuRadioGroup
              value={String(pageSize)}
              onValueChange={(size) =>
                table.setPagination({ pageIndex: 0, pageSize: Number(size) })
              }
            >
              {pageSizeOptions.map((size) => (
                <DropdownMenuRadioItem key={size} value={String(size)}>
                  {size}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <span className="tabular-nums">
        {rowCount === 0
          ? t('indexTable.noResults')
          : t('indexTable.pagination.range', { from, to, count: rowCount })}
      </span>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="icon-sm"
          aria-label={t('table.previousPage')}
          disabled={pageIndex === 0}
          onClick={() => go(pageIndex - 1)}
        >
          <ChevronLeftIcon aria-hidden />
        </Button>
        <span className="px-1 tabular-nums">
          {t('indexTable.pagination.page', { page: pageIndex + 1, count: pageCount })}
        </span>
        <Button
          variant="outline"
          size="icon-sm"
          aria-label={t('table.nextPage')}
          disabled={pageIndex >= pageCount - 1}
          onClick={() => go(pageIndex + 1)}
        >
          <ChevronRightIcon aria-hidden />
        </Button>
      </div>
    </nav>
  );
}

export { DataTablePagination };
